const _ = require("lodash");

// GridGeometry is an implementation of a generic grid geometry interface that
// can be used by things like griddy grid. Multiple grid styles are possible;
// see makeCore for what is currently implemented. To implement a new style of
// geometry, check SquareGridGeometryCore, and make a file that implements all
// the same functions. Then add it to makeCore.

// The GridGeometry object is an immutable structure that holds the grid style,
// size and options. It also has an {x, y, size} object called dim providing it
// with a coordinate system that the drawing and mouse functions use. To change
// the style/size/options, you need to create a new Grid Geometry object. If
// you need a different dim, use the methods withDim or withDimFromRect to
// clone the geometry with a different coordinate system without changing the
// original object.

// Cells and vertices are always [x, y]. Edges are always [x, y, z], where z is
// some style-dependent value to distinguish between kinds of edges.
// Cell/edge/vertex keys are obtained by doing thing.join(",").

// Feel free to use geometry.dim.size to make artificial padding or nudging
// when writing drawing code that uses this.

const SquareGridGeometryCore = require("./SquareGridGeometryCore");
const TriangleGridGeometryCore = require("./TriangleGridGeometryCore");
const HexagonGridRowsGeometryCore = require("./HexagonGridRowsGeometryCore");
const HexagonGridColumnsGeometryCore = require("./HexagonGridColumnsGeometryCore");

const makeCore = function(gridStyle, cols, rows, options) {
	if (gridStyle === "square") {
		return new SquareGridGeometryCore(cols, rows, options);
	} else if (gridStyle === "tri") {
		return new TriangleGridGeometryCore(cols, rows, options);
	} else if (gridStyle === "hexRows") {
		return new HexagonGridRowsGeometryCore(cols, rows, options);
	} else if (gridStyle === "hexColumns") {
		return new HexagonGridColumnsGeometryCore(cols, rows, options);
	} else {
		throw new Error("Unsupported geometry " + gridStyle);
	}
};

// Given an array of arrays, where the nested arrays contain only things that
// can be losslessly converted to strings, return the same array with all
// duplicates removed.
const nestedUnique = function(aList) {
	const seen = new Set();
	const result = [];
	aList.forEach(a => {
		const key = a.join(",");
		if (!seen.has(key)) {
			seen.add(key);
			result.push(a);
		}
	});
	return result;
};

const toElSet = function(elList) {
	const s = new Set();
	elList.map(el => el.join(",")).forEach(x => s.add(x));
	return s;
};

const inElSet = function(elSet, el) {
	return elSet.has(el.join(","));
};

// Preserves order of elList1
const intersectElArrays = function(elList1, elList2) {
	const elSet2 = toElSet(elList2);
	return elList1.filter(el => inElSet(elSet2, el));
};

// Return whether a given cell is outside the given grid geometry.
const isOutside = function(geom, x, y) {
	if (!geom._cache.outSet) {
		geom._cache.outSet = toElSet(geom.outsideCells || []);
	}
	return inElSet(geom._cache.outSet, [x, y]);
};

const invertRectIfNeeded = function(geom, rect) {
	if (geom.invertY) {
		const fullRect = geom.getDrawFullRect();
		const y = 2 * fullRect.y + fullRect.h - rect.y - rect.h;
		return {x: rect.x, y, w: rect.w, h: rect.h};
	}
	return rect;
};

const invertPointIfNeeded = function(geom, p) {
	if (geom.invertY) {
		const fullRect = geom.getDrawFullRect();
		return [p[0], 2 * fullRect.y + fullRect.h - p[1]];
	}
	return p;
};

const getCollinearBundle = function(geom) {
	if (geom._cache.collBundle) {
		return geom._cache.collBundle;
	}
	const seenEdges = new Set();
	const bundle = [];
	geom.getAllEdges().forEach(e => {
		if (seenEdges.has(e.join(","))) {
			return;
		}
		const collEdges = geom.getCollinearEdges(e[0], e[1], e[2]);
		collEdges.forEach(ce => seenEdges.add(ce.join(",")));
		bundle.push(collEdges);
	});
	geom._cache.collBundle = bundle;
	return bundle;
};

// Return the element of the candidates array which returns the highest number
// when passed through the getScore function. score can return a non-number
// like null or false to indicate not to consider that element.
const findBest = function(candidates, getScore) {
	let bestI;
	let bestScore = -Infinity;
	for (let i = 0; i < candidates.length; i += 1) {
		const score = getScore(candidates[i]);
		if (typeof score === "number" && score > bestScore) {
			bestScore = score;
			bestI = i;
		}
	}
	return candidates[bestI] || null;
};

/**
 * Constructor. In addition to any options allowed by the grid style, you can
 * also pass invertY and outsideCells. invertY makes it so that the positive y
 * direction goes to the top of the screen in mouse/draw coordinates (other
 * coordinates are unaffected). outsideCells is an optional array of [x, y]
 * cells which should not be considered a part of the grid.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const GridGeometry = function(gridStyle, cols, rows, options, dim) {
	this.style = gridStyle;
	this.core = makeCore(gridStyle, cols, rows, options);
	this.cols = cols;
	this.rows = rows;
	this.options = options || {};
	this.dim = dim || {x: 0, y: 0, size: 1};
	this.outsideCells = this.options.outsideCells;
	this.invertY = !!this.options.invertY;
	// Object containing memoized returns of this.core methods. All stored under
	// one object so that it can be transferred to clones.
	this._cache = {};
};

//
// dim setters and computation
//

// Returns a dim object which fits this grid in the given {x,y,w,h} rectangle.
// Optionally pass a maxSize, the maximum size in the returned dim, and margin,
// the minimum amount removed from each edge of the rectangle as a coefficient
// of the returned size.
GridGeometry.prototype.computeDimFromRect = function(rect, maxSize, margin) {
	maxSize = maxSize || Infinity;
	margin = margin || 0;
	// Dim-independent size of grid, as determined by core.
	const baseW = this.core.getBaseWidth();
	const baseH = this.core.getBaseHeight();
	// Size to use if width were the limiting factor
	const wSize = rect.w / (baseW + 2 * margin);
	// Size to use if height were the limiting factor
	const hSize = rect.h / (baseH + 2 * margin);
	// Actual size to use
	const size = Math.min(maxSize, wSize, hSize);
	// Determine the width and height occupied by a grid of this size, with dim
	// accounted for.
	const fullW = baseW * size;
	const fullH = baseH * size;
	return {
		x: rect.x + (rect.w - fullW) / 2,
		y: rect.y + (rect.h - fullH) / 2,
		size,
	};
};

// Return a clone of this geometry with the dim value replaced.
GridGeometry.prototype.withDim = function(dim) {
	const g = new GridGeometry(
		this.style,
		this.cols,
		this.rows,
		this.options,
		dim
	);
	g._cache = this._cache;
	return g;
};

// Return a clone of this geometry with the dim value replaced. The new dim
// is what computeDimFromRect would return given these arguments.
GridGeometry.prototype.withDimFromRect = function(rect, maxSize, margin) {
	return this.withDim(this.computeDimFromRect(rect, maxSize, margin));
};

//
// Pure geometry methods that do not interact with this.dim.
//

// Returns an array of [x, y] coordinates that identify each cell.
GridGeometry.prototype.getAllCells = function() {
	this._cache.allC =
		this._cache.allC ||
		this.core.getAllCells().filter(c => !isOutside(this, c[0], c[1]));
	return this._cache.allC;
};

// Returns an array of [x, y, z] coordinates that identify each edge.
GridGeometry.prototype.getAllEdges = function() {
	this._cache.allE =
		this._cache.allE ||
		nestedUnique(
			_.flatMap(this.getAllCells(), c => {
				return this.getCellEdges(c[0], c[1]);
			})
		);
	return this._cache.allE;
};

// Returns an array of [x, y] coordinates that identify each vertex.
GridGeometry.prototype.getAllVertices = function() {
	this._cache.allV =
		this._cache.allV ||
		nestedUnique(
			_.flatMap(this.getAllCells(), c => {
				return this.getCellVertices(c[0], c[1]);
			})
		);
	return this._cache.allV;
};

// Returns true if the cell is in the grid. Respects outside cells.
GridGeometry.prototype.isCellInGrid = function(x, y) {
	this._cache.allCSet = this._cache.allCSet || toElSet(this.getAllCells());
	return inElSet(this._cache.allCSet, [x, y]);
};

// Returns true if the edge is in the grid. Respects outside cells: an edge
// must touch at least one non-outside cell in the grid.
GridGeometry.prototype.isEdgeInGrid = function(x, y, z) {
	this._cache.allESet = this._cache.allESet || toElSet(this.getAllEdges());
	return inElSet(this._cache.allESet, [x, y, z]);
};

// Returns true if the vertex is in the grid. Respects outside cells: a vertex
// must touch at least one non-outside cell in the grid.
GridGeometry.prototype.isVertexInGrid = function(x, y) {
	this._cache.allVSet = this._cache.allVSet || toElSet(this.getAllVertices());
	return inElSet(this._cache.allVSet, [x, y]);
};

// Converts string key like "1,2" to [1,2]
GridGeometry.prototype.convertCellKey = function(key) {
	if (!key) {
		return null;
	}
	const keyCoords = key.split(",");
	return [parseInt(keyCoords[0]), parseInt(keyCoords[1])];
};

// Converts string key like "1,2,false" to [1,2,false]
GridGeometry.prototype.convertEdgeKey = function(key) {
	if (!key) {
		return null;
	}
	const keyCoords = key.split(",");
	return [
		parseInt(keyCoords[0]),
		parseInt(keyCoords[1]),
		this.core.convertEdgeZ(keyCoords[2]),
	];
};

// Converts string key like "1,2" to [1,2]
GridGeometry.prototype.convertVertexKey = function(key) {
	if (!key) {
		return null;
	}
	const keyCoords = key.split(",");
	return [parseInt(keyCoords[0]), parseInt(keyCoords[1])];
};

//get all edges that do not overlap (border edges)
GridGeometry.prototype.getAllNonOverlapEdges = function() {
	return this.core.getAllNonOverlapEdges(this);
};

GridGeometry.prototype.getIntersectingEdgeBetweenTwoCells = function(
	cell1,
	cell2
) {
	return this.core.getIntersectingEdgeBetweenTwoCells(this, cell1, cell2);
};

// Returns an array of [x, y, z] edges forming the boundary of this edge in
// clockwise order.
GridGeometry.prototype.getCellEdges = function(x, y) {
	return this.core.getCellEdges(x, y);
};

// Same as getCellEdges, but returns the string key form instead.
GridGeometry.prototype.getCellEdgeKeys = function(x, y) {
	return this.getCellEdges(x, y).map(e => e.join(","));
};

// Returns an array of [x, y] vertices in clockwise order, with each
// consecutive pair of vertices forming one of the cell's edges.
GridGeometry.prototype.getCellVertices = function(x, y) {
	const vPairs = this.getCellEdges(x, y).map(edge => {
		return this.getEdgeVertices(edge[0], edge[1], edge[2]);
	});
	// To ensure we get all vertices in the correct order, we determine the first
	// vertex for sure. nestedUnique's properties take care of the rest.
	vPairs[0] = intersectElArrays(vPairs[0], vPairs[vPairs.length - 1]);
	return nestedUnique(_.flatten(vPairs));
};

// Returns an array of two [x, y] cells that the edge separates.
GridGeometry.prototype.getEdgeCells = function(x, y, z) {
	return this.core.getEdgeCells(x, y, z);
};

// Returns an array of two [x, y] vertices that the edge joins.
GridGeometry.prototype.getEdgeVertices = function(x, y, z) {
	return this.core.getEdgeVertices(x, y, z);
};

// Returns an array of [x, y] cells touching this vertex in clockwise order.
GridGeometry.prototype.getVertexCells = function(x, y) {
	const cPairs = this.getVertexEdges(x, y).map(edge => {
		return this.getEdgeCells(edge[0], edge[1], edge[2]);
	});
	// To ensure we get all cells in clockwise order, we determine the first cell
	// for sure. nestedUnique's properties take care of the rest.
	cPairs[0] = intersectElArrays(cPairs[0], cPairs[cPairs.length - 1]);
	return nestedUnique(_.flatten(cPairs));
};

// Returns an array of [x, y, z] edges touching this vertex in clockwise order.
GridGeometry.prototype.getVertexEdges = function(x, y) {
	return this.core.getVertexEdges(x, y);
};

// Return the other cell on the edge. Assumes cell is on the edge.
GridGeometry.prototype.getOtherCell = function(edge, cell) {
	const cs = this.getEdgeCells(edge[0], edge[1], edge[2]);
	return cs[0][0] === cell[0] && cs[0][1] === cell[1] ? cs[1] : cs[0];
};

// Return the other vertex on the edge. Assumes vertex is on the edge.
GridGeometry.prototype.getOtherVertex = function(edge, vertex) {
	const vs = this.getEdgeVertices(edge[0], edge[1], edge[2]);
	return vs[0][0] === vertex[0] && vs[0][1] === vertex[1] ? vs[1] : vs[0];
};

// Returns the edge [x, y, z] that contains these two vertices.
// If there isn't one, returns null.
GridGeometry.prototype.getEdgeWithVertices = function(x1, y1, x2, y2) {
	if (x1 === x2 && y1 === y2) {
		return null;
	}
	const cands = intersectElArrays(
		this.getVertexEdges(x1, y1),
		this.getVertexEdges(x2, y2)
	);
	return cands.length ? cands[0] : null;
};

// Returns whether the edge lies completely inside the grid.
GridGeometry.prototype.isEdgeInsideGrid = function(x, y, z) {
	return !this.isEdgeOutsideGrid(x, y, z) && !this.isEdgeOnGridBorder(x, y, z);
};

// Returns whether the edge lies completely outside the grid. Always returns
// the not of isEdgeInGrid but the name is clearer.
GridGeometry.prototype.isEdgeOutsideGrid = function(x, y, z) {
	return !this.isEdgeInGrid(x, y, z);
};

// Returns whether the edge touches both an inside and outside cell.
GridGeometry.prototype.isEdgeOnGridBorder = function(x, y, z) {
	const inside = this.getEdgeCells(x, y, z).map(cell =>
		this.isCellInGrid(cell[0], cell[1])
	);
	return inside[0] !== inside[1];
};

// Returns whether the two [x, y, z] edges intersect. Also returns true if the
// edges both exist and are equal.
GridGeometry.prototype.doEdgesIntersect = function(e1, e2) {
	return (e1 && _.isEqual(e1, e2)) || !!this.getEdgeIntersection(e1, e2);
};

// Returns the [x, y] vertex the two edges have in common. If the edges are
// the same, do not both exist, or no common vertex exists, return null.
GridGeometry.prototype.getEdgeIntersection = function(e1, e2) {
	if (!e1 || !e2) {
		return null;
	}
	const cands = intersectElArrays(
		this.getEdgeVertices(e1[0], e1[1], e1[2]),
		this.getEdgeVertices(e2[0], e2[1], e2[2])
	);
	return cands.length === 1 ? cands[0] : null;
};

// Returns the largest array of edges containing the given one which form
// a consecutive line of edges in the grid. Consecutive elements of the
// returned array are consecutive along the line.
GridGeometry.prototype.getCollinearEdges = function(x, y, z) {
	this._cache.collEdges = this._cache.collEdges || {};
	const cacheKey = x + "," + y + "," + z;
	if (this._cache.collEdges[cacheKey]) {
		return this._cache.collEdges[cacheKey];
	}

	// Given an edge and a vertex on it, return an array of all edges found by
	// repeatedly taking getCollinearEdge. Does not include e in return.
	const getEdgeChain = (e, v) => {
		const nextE = this.core.getCollinearEdge(e, v);
		if (!nextE || this.isEdgeOutsideGrid(nextE[0], nextE[1], nextE[2])) {
			return [];
		}
		const nextV = this.getOtherVertex(nextE, v);
		const ret = getEdgeChain(nextE, nextV);
		ret.unshift(nextE);
		return ret;
	};

	const [baseV1, baseV2] = this.getEdgeVertices(x, y, z);
	const ret = _.reverse(getEdgeChain([x, y, z], baseV1))
		.concat([[x, y, z]])
		.concat(getEdgeChain([x, y, z], baseV2));

	ret.forEach(e => {
		this._cache.collEdges[e.join(",")] = ret;
	});
	return ret;
};

// Returns whether a given edge contains a given vertex
GridGeometry.prototype.doesEdgeContainVertex = function(edge, vertex) {
	const edgeVertices = this.getEdgeVertices(edge[0], edge[1], edge[2]);
	return inElSet(toElSet(edgeVertices), vertex);
};

// Returns new edge after we rotate given edge about the vertex. Returns null
// if the edge or vertex are missing, or if the vertex is not on the edge.
// The returned edge is the next one around the vertex.
GridGeometry.prototype.rotateEdgeAboutVertex = function(
	edge,
	vertex,
	isClockwise
) {
	if (!edge || !vertex) {
		return null;
	}
	const edgeKeys = this.getVertexEdges(vertex[0], vertex[1]).map(e =>
		e.join(",")
	);
	const edgeInd = edgeKeys.indexOf(edge.join(","));
	if (edgeInd === -1) {
		return null;
	}
	const baseInd = edgeInd + (isClockwise ? 1 : -1);
	const rotatedKey = edgeKeys[(baseInd + edgeKeys.length) % edgeKeys.length];
	return this.convertEdgeKey(rotatedKey);
};

// Given x,y coordinates returned from mouseToGrid (so dim-independent),
// force them inside the grid and return the result.
GridGeometry.prototype.clampPosToGrid = function(x, y) {
	// We have to change to mouse/draw coordinates and back to account for
	// the dim-independent coordinates being distorted.
	const tempDim = {x: 0, y: 0, size: 1};
	const [drawX, drawY] = this.core.gridToMouse(tempDim, x, y);
	// Slightly reduce the maximum position so that when we detect cells after
	// using this method, we detect a cell inside the grid.
	const [clampedX, clampedY] = [
		Math.min(this.core.getBaseWidth() - 0.01, Math.max(0, drawX)),
		Math.min(this.core.getBaseHeight() - 0.01, Math.max(0, drawY)),
	];
	return this.core.mouseToGrid(tempDim, clampedX, clampedY);
};

//
// Drawing and mouse methods that depend on this.dim.
//

// Returns an {x, y, w, h} rectangle in dim coordinates.
GridGeometry.prototype.getDrawFullRect = function() {
	return {
		x: this.dim.x,
		y: this.dim.y,
		w: this.dim.size * this.core.getBaseWidth(),
		h: this.dim.size * this.core.getBaseHeight(),
	};
};

GridGeometry.prototype.getEdgeLabelRect = function(
	col,
	row,
	width,
	height,
	pad
) {
	// get top-left vertex canvas coords
	const [vertexX, vertexY] = this.getDrawEdge(col, row);

	if (col === 0) {
		// if on left side, put label on left
		const x = vertexX - width - pad;
		const y = vertexY - height / 2;

		return [x, y, width, height];
	} else {
		// else on right side, put label on right of vertex
		const x = vertexX + pad;
		const y = vertexY - height / 2;

		return [x, y, width, height];
	}
};

GridGeometry.prototype.getBottomLabelRect = function(
	labelWidth,
	labelHeight,
	subtitleWidth,
	subtitleHeight,
	gridPad, // padding between elements of grid
	labelPad // padding between labels
) {
	// get bottom left, bottom right vertices
	const leftVertex = this.getDrawVertex(0, this.rows);
	const leftX = leftVertex[0];
	let y = leftVertex[1];
	const [rightX] = this.getDrawVertex(this.cols, this.rows);

	// calculate
	let subtitleRect;
	y += gridPad;
	if (subtitleWidth === 0 || subtitleHeight === 0) {
		subtitleRect = [0, 0, 0, 0];
	} else {
		subtitleRect = [
			leftX + (rightX - leftX - subtitleWidth) / 2,
			y,
			subtitleWidth,
			subtitleHeight,
		];
	}

	y += subtitleHeight;
	const titleRect = [
		leftX + (rightX - leftX - labelWidth) / 2,
		y,
		labelWidth,
		labelHeight,
	];

	return [titleRect, subtitleRect];
};

// Returns the mouse coordinates divided by the dim size.
// TODO: The existing usage maybe only works in square grids.
GridGeometry.prototype.scaleMouseOffset = function(mouseX, mouseY) {
	return [mouseX / this.dim.size, mouseY / this.dim.size];
};

// Returns dim-independent grid coordinates from mouse coordinates.
GridGeometry.prototype.mouseToGrid = function(mouseX, mouseY, options) {
	options = options || {};
	const [realX, realY] = invertPointIfNeeded(this, [mouseX, mouseY]);
	let p = this.core.mouseToGrid(this.dim, realX, realY);
	if (options.clamp) {
		p = this.clampPosToGrid(p[0], p[1]);
	}
	return p;
};

// Returns mouse coordinates from dim-independent grid coordinates.
GridGeometry.prototype.gridToMouse = function(coordX, coordY) {
	return invertPointIfNeeded(
		this,
		this.core.gridToMouse(this.dim, coordX, coordY)
	);
};

// Returns mouse coordinates modified to be inside draw rectangle.
GridGeometry.prototype.clampMouseToGrid = function(mouseX, mouseY) {
	const rect = this.getDrawFullRect();
	return [
		Math.max(Math.min(mouseX, rect.x + rect.w), rect.x),
		Math.max(Math.min(mouseY, rect.y + rect.h), rect.y),
	];
};

// Returns an {x, y, w, h} rectangle in dim coordinates. The rectangle may
// not be fully contained in the cell, but no two rectangles will overlap.
GridGeometry.prototype.getDrawCellRect = function(x, y) {
	return invertRectIfNeeded(this, this.core.getDrawCellRect(this.dim, x, y));
};

// Returns an [x, y] array in dim coordinates.
GridGeometry.prototype.getDrawCellCenter = function(x, y) {
	return invertPointIfNeeded(this, this.core.getDrawCellCenter(this.dim, x, y));
};

// Returns an [x1, y1, x2, y2] array.
GridGeometry.prototype.getDrawEdge = function(x, y, z) {
	const vs = this.getEdgeVertices(x, y, z);
	const vDraw = vs.map(v => this.getDrawVertex(v[0], v[1]));
	return [vDraw[0][0], vDraw[0][1], vDraw[1][0], vDraw[1][1]];
};

// Returns an [x, y] array.
GridGeometry.prototype.getDrawVertex = function(x, y) {
	return invertPointIfNeeded(this, this.core.getDrawVertex(this.dim, x, y));
};

// Returns an array of [x1, y1, x2, y2] arrays specifying lines to draw to
// form the whole grid.
GridGeometry.prototype.getDrawGridlines = function() {
	return getCollinearBundle(this).map(collEdges => {
		const firstCE = collEdges[0];
		const lastCE = collEdges[collEdges.length - 1];
		const [x1, y1] = this.getDrawEdge(firstCE[0], firstCE[1], firstCE[2]).slice(
			0,
			2
		);
		const [x2, y2] = this.getDrawEdge(lastCE[0], lastCE[1], lastCE[2]).slice(2);
		return [x1, y1, x2, y2];
	});
};

// Returns an array of [x1, y1, x2, y2] arrays specifying lines to draw to
// form the grid without the specified removed edges.
GridGeometry.prototype.getPartialGridlines = function(removedEdges) {
	return this.getAllEdges()
		.filter(edge => !removedEdges.includes(edge.join(",")))
		.map(([c, r, dir]) => this.getDrawEdge(c, r, dir));
};

// Returns whether the mouse has moved far away enough from a previous edge for
// the new one to count.
GridGeometry.prototype.isNewEdge = function(mouseX, mouseY, newEdge, lastEdge) {
	if (!this.doEdgesIntersect(newEdge, lastEdge)) {
		return true;
	}
	const [coordX, coordY] = this.mouseToGrid(mouseX, mouseY);
	return this.core.isNewEdge(coordX, coordY, newEdge, lastEdge);
};

// Returns a vertex close to the coordinates provided, or null if none close enough. options
// can include forceInside, allowOutside.
// Note - this can be used to get cell from any coordinates, not just mouse coordinates
GridGeometry.prototype.getMousedCell = function(mouseX, mouseY, options) {
	options = options || {};
	const coords = this.mouseToGrid(mouseX, mouseY, {clamp: options.forceInside});
	const best = this.core.getMousedCell(
		coords[0],
		coords[1],
		options.forceInside
	);
	if (best && !options.allowOutside && !this.isCellInGrid(best[0], best[1])) {
		return null;
	}
	return best || null;
};

// Returns a vertex close to the mouse, or null if none close enough. options
// can include forceInside, allowOutside, disallowBorder, deadCenterDistance,
// deadVertexDistance.
// The dead distances are roughly 0-1 values. A dead region is one where the
// mouse is not considered to be mousing any edge. deadCenterDistance is how
// much of the cell is dead, and deadVertexDistance is how much of the area
// surrounding the vertex is dead. The meaning of the value depends on the
// geometry style.
// TODO: Distance 1 for square means everything is dead. Have all other
// geometries do the same?
GridGeometry.prototype.getMousedEdge = function(mouseX, mouseY, options) {
	if (isNaN(mouseX) || isNaN(mouseY)) {
		return null;
	}
	options = options || {};
	const coords = this.mouseToGrid(mouseX, mouseY, {clamp: options.forceInside});
	const closestCell = this.getMousedCell(mouseX, mouseY, {
		forceInside: options.forceInside,
		allowOutside: true,
	});
	if (options.deadCenterDistance > 0) {
		const drawCenter = this.getDrawCellCenter(closestCell[0], closestCell[1]);
		const coordCenter = this.mouseToGrid(drawCenter[0], drawCenter[1]);
		if (
			this.core.isDeadCenter(
				options.deadCenterDistance,
				coords[0],
				coords[1],
				coordCenter[0],
				coordCenter[1]
			)
		) {
			return null;
		}
	}
	if (options.deadVertexDistance > 0) {
		const closestVertex = this.getMousedVertex(mouseX, mouseY, {
			forceInside: options.forceInside,
			allowOutside: true,
			closestCell,
		});
		const drawVertex = this.getDrawVertex(closestVertex[0], closestVertex[1]);
		const coordVertex = this.mouseToGrid(drawVertex[0], drawVertex[1]);
		if (
			this.core.isDeadVertex(
				options.deadVertexDistance,
				coords[0],
				coords[1],
				coordVertex[0],
				coordVertex[1]
			)
		) {
			return null;
		}
	}
	const candidates = this.getCellEdges(closestCell[0], closestCell[1]);
	const best = findBest(candidates, cand => {
		const [x1, y1, x2, y2] = this.getDrawEdge(cand[0], cand[1], cand[2]);
		const [mx, my] = [(x1 + x2) / 2, (y1 + y2) / 2];
		const dist = (mx - mouseX) * (mx - mouseX) + (my - mouseY) * (my - mouseY);
		return -dist;
	});
	if (
		best &&
		!options.allowOutside &&
		!this.isEdgeInGrid(best[0], best[1], best[2])
	) {
		return null;
	} else if (
		best &&
		options.disallowBorder &&
		this.isEdgeOnGridBorder(best[0], best[1], best[2])
	) {
		return null;
	}
	return best || null;
};

// Returns a vertex close to the mouse, or null if none close enough. options
// can include forceInside, allowOutside, tight. (Also can include closestCell,
// but this should be used as an optimization only.)
GridGeometry.prototype.getMousedVertex = function(mouseX, mouseY, options) {
	options = options || {};
	const [coordX, coordY] = this.mouseToGrid(mouseX, mouseY, {
		clamp: options.forceInside,
	});
	const closestCell =
		options.closestCell ||
		this.getMousedCell(mouseX, mouseY, {
			forceInside: options.forceInside,
			allowOutside: true,
		});
	const candidates = this.getCellVertices(closestCell[0], closestCell[1]);
	const best = findBest(candidates, cand => {
		const [vx, vy] = this.getDrawVertex(cand[0], cand[1]);
		const [vgx, vgy] = this.mouseToGrid(vx, vy);
		const looseFit = this.core.isLooseVertexFit(coordX, coordY, vgx, vgy);
		if (!options.tight || !looseFit) {
			const dist =
				(vx - mouseX) * (vx - mouseX) + (vy - mouseY) * (vy - mouseY);
			return -dist;
		}
		return null;
	});
	if (best && !options.allowOutside && !this.isVertexInGrid(best[0], best[1])) {
		return null;
	}
	return best || null;
};

// Returns two dimensional array of closest edges [x, y, z] that intersect near
// the moused vertex. options can include forceInside, allowOutside,
// disallowBorder.
GridGeometry.prototype.getMousedCorner = function(mouseX, mouseY, options) {
	options = options || {};
	const cvOpts = {forceInside: options.forceInside, allowOutside: true};
	const cell = this.getMousedCell(mouseX, mouseY, cvOpts);
	const vertex = this.getMousedVertex(mouseX, mouseY, cvOpts);
	let ret = intersectElArrays(
		this.getCellEdges(cell[0], cell[1]),
		this.getVertexEdges(vertex[0], vertex[1])
	);
	if (!options.allowOutside) {
		ret = ret.map(e => (this.isEdgeOutsideGrid(e[0], e[1], e[2]) ? null : e));
	}
	if (options.disallowBorder) {
		ret = ret.map(e => (this.isEdgeOnGridBorder(e[0], e[1], e[2]) ? null : e));
	}
	return ret;
};

// If mouse is close to a path, returns an array containing the
// coordinates of both cells and direction.
// For square paths, the fourth element = whether path is horizontal.
// Otherwise, returns null.
GridGeometry.prototype.getMousedCellPath = function(mouseX, mouseY, options) {
	const edge = this.getMousedEdge(mouseX, mouseY, options);
	if (!edge) {
		return null;
	}
	let direction = null;
	if (this.core.style === "square") {
		direction = !edge[2];
	}
	return this.getEdgeCells(edge[0], edge[1], edge[2]).concat([direction]);
};

module.exports = GridGeometry;
